import React, { useEffect, useState, useMemo } from 'react';
import moment from 'moment/moment';
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  TextField,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import isUrl from 'is-url';
import startCase from 'lodash/startCase';
import { gql, useQuery } from '@apollo/client';
import { normalizeUrl } from '@pv/common/utils';
import { EditablePanel, Fields } from '@pv/common/components';
import { useMutation } from '@pv/common/hooks';
import {
  planDefinitionsQuery,
  adminUpdateVenueMutation,
} from '../../../components/VenueDrawer';
import { HubspotOwners } from '../../Organization/components/HubspotOwners';

const paymentProviderVals = [
  ['stripe', 'Stripe'],
  ['square', 'Square'],
];

const updateVenuePlanMutation = gql`
  mutation UpdateVenuePlanAdmin($input: UpdateVenuePlanInput!) {
    updateVenuePlan(input: $input) {
      errors {
        message
      }
    }
  }
`;

const adminValidTimezonesQuery = gql`
  query AdminValidTimezones {
    adminValidTimezones
  }
`;

export const InfoPanel = ({ venue, availablePlanSets }) => {
  const subscription = venue.subscription;
  const [name, setName] = useState(venue.name);
  const [trialEndDate, setTrialEndDate] = useState(
    venue?.trialEndDate && moment(venue?.trialEndDate)
  );
  const [slug, setSlug] = useState(venue.slug || '');
  const [websiteUrl, setWebsiteUrl] = useState(venue.websiteUrl || '');
  const [achEnabled, setAchEnabled] = useState(venue.achEnabled || false);
  const [liveAvailability, setLiveAvailability] = useState(
    venue.liveAvailability || false
  );
  const [paymentsEnabled, setPaymentsEnabled] = useState(
    venue.paymentsEnabled || false
  );
  const [planSetId, setPlanSetId] = useState(venue.planSetId || '');
  const [defaultPaymentProvider, setDefaultPaymentProvider] = useState(
    venue.defaultPaymentProvider || 'stripe'
  );
  const [googlePlaceId, setGooglePlaceId] = useState(
    venue.googlePlaceInfo?.placeId || ''
  );
  const [timezone, setTimezone] = useState(venue.timezone || '');
  const [forceWebsiteError, setForceWebsiteError] = useState(false);
  const [planName, setPlanName] = useState(startCase(subscription?.tier || ''));

  // =================================================================

  const monthlyRate = (subscription?.monthlyRate || 0) / 100;
  const {
    basicFeaturesEnabled,
    proFeaturesEnabled,
    premiumFeaturesEnabled,
    enterpriseFeaturesEnabled,
  } = venue?.featureScope || {};

  const cardApplicationFeeRate = subscription?.cardApplicationFeeRate;
  const achApplicationFeeRate = subscription?.achApplicationFeeRate;
  const customerStartDate = venue.customerStartedAt || subscription?.startDate;
  const customerStartedAt = customerStartDate
    ? moment(customerStartDate)
    : null;
  const billingInterval = subscription?.billingInterval;

  const { data: adminPlanDefinitions } = useQuery(planDefinitionsQuery);
  const { data: adminValidTimezonesData, loading: loadingTimezones } = useQuery(
    adminValidTimezonesQuery
  );
  const planDefinitions = adminPlanDefinitions?.adminPlanDefinitions;
  const timezoneOptions = useMemo(
    () =>
      Array.from(
        new Set([
          ...(adminValidTimezonesData?.adminValidTimezones || []),
          venue.timezone,
        ]).values()
      ),
    [adminValidTimezonesData?.adminValidTimezones, venue.timezone]
  );
  const planNameOptions = useMemo(() => {
    const planNames =
      subscription?.tier === 'starter'
        ? ['starter']
        : ['starter', `${subscription?.tier}`];

    return planNames.map((tier) => startCase(tier));
  }, [subscription?.tier]);
  const starterPlan = useMemo(() => {
    return planDefinitions?.find((p) => p.tier === 'starter');
  }, [planDefinitions]);
  const availablePlanSetVals = availablePlanSets?.map((planSet) => [
    planSet.id,
    planSet.name,
  ]);

  const [updateVenuePlan] = useMutation(updateVenuePlanMutation);

  const [updateVenue, { loading }] = useMutation(adminUpdateVenueMutation(), {
    onNoErrorsCompleted: () => {
      enqueueSnackbar('Updated Venue', { variant: 'success' });
    },
  });

  const saveVenue = () => {
    const input = {
      id: venue.id,
      name,
      slug,
      defaultPaymentProvider,
      achEnabled,
      liveAvailability,
      paymentsEnabled,
      googlePlaceId,
      planSetId: parseInt(planSetId),
      timezone,
    };

    if (trialEndDate) {
      input.trialEndDate = trialEndDate.format('YYYY-MM-DD');
    }

    if (websiteUrl) {
      if (isUrl(websiteUrl)) {
        const url = normalizeUrl(websiteUrl);
        setWebsiteUrl(url);
        input.websiteUrl = url;
      } else {
        setForceWebsiteError(true);
        enqueueSnackbar('Website is invalid', { variant: 'error' });
        return false;
      }
    }
    const variables = { input };
    updateVenue({ variables });
  };

  const downgradeToStarterPlan = async () => {
    const planDefinitionId = starterPlan?.id;
    const input = {
      id: venue.id,
      planDefinitionId,
    };
    const variables = { input };
    updateVenuePlan({ variables });
  };

  const onSave = () => {
    if (planName !== startCase(subscription?.tier)) {
      downgradeToStarterPlan();
    }
    saveVenue();
  };

  const resetValues = () => {
    setName(venue.name || '');
    setPlanSetId(venue.planSet?.id || '');
    setPlanName(startCase(venue.subscription?.tier) || '');
    setWebsiteUrl(venue.websiteUrl);
    setSlug(venue.slug);
    setAchEnabled(venue.achEnabled || false);
    setLiveAvailability(venue.liveAvailability || false);
    setPaymentsEnabled(venue.paymentsEnabled || false);
    setDefaultPaymentProvider(venue.defaultPaymentProvider);
    setGooglePlaceId(venue.googlePlaceInfo?.placeId || '');
    setTimezone(venue.timezone || '');
  };

  useEffect(resetValues, [venue]);

  const maybeNormalizeWebsiteUrl = () => {
    if (websiteUrl) {
      setWebsiteUrl(normalizeUrl(websiteUrl));
    }
  };

  const onChangeWebsiteUrl = (e) => {
    const { value } = e.target;
    if (!value) {
      setForceWebsiteError(false);
    }
    setWebsiteUrl(value);
  };

  return (
    <div>
      <EditablePanel
        name="account"
        onSave={onSave}
        onClear={resetValues}
        loading={loading}
        title="Venue Info"
      >
        {({ editing }) => (
          <Grid container spacing={2}>
            {venue.organization.hubspotDealId && (
              <Grid item xs={12}>
                <HubspotOwners organizationId={venue.organization.id} />
              </Grid>
            )}
            <Grid item xs={6}>
              <Fields.PvTextField
                disabled={!editing}
                required
                label="Name"
                value={name || ''}
                onChange={(e) => setName(e.target.value)}
                name="name"
              />
            </Grid>
            <Grid item xs={6}>
              <Fields.PvTextField
                disabled={!editing}
                required
                label="Slug"
                value={slug || ''}
                onChange={(e) => setSlug(e.target.value)}
                name="slug"
              />
            </Grid>
            <Grid item xs={6}>
              <Fields.PvTextField
                disabled={!editing}
                label="Website"
                value={websiteUrl || ''}
                onChange={onChangeWebsiteUrl}
                name="website-url"
                forceError={forceWebsiteError}
                onBlur={maybeNormalizeWebsiteUrl}
              />
            </Grid>
            <Grid item xs={6}>
              <Fields.PvTextField
                disabled={!editing}
                label="Google Place ID"
                value={googlePlaceId || ''}
                onChange={(e) => setGooglePlaceId(e.target.value)}
                name="google-place-id"
                helperText={
                  <>
                    Google Maps Place ID for reviews integration.{' '}
                    <a
                      href="https://developers.google.com/maps/documentation/places/web-service/place-id#find-id"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Find Place ID
                    </a>
                  </>
                }
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                disabled={!editing}
                disableClearable
                fullWidth
                loading={loadingTimezones}
                options={timezoneOptions}
                value={timezone || null}
                onChange={(_, newValue) => setTimezone(newValue || timezone)}
                isOptionEqualToValue={(option, value) => option === value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Timezone"
                    name="timezone"
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Fields.PvDropDown
                disabled={!editing}
                label="Plan Name"
                name="plan"
                value={planName || ''}
                vals={planNameOptions}
                onChange={(e) => setPlanName(e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              {/* TODO: Properly update field to read-only */}
              <Fields.PvDropDown
                disabled={true}
                label="Billing Interval"
                name="billingInterval"
                value={billingInterval || ''}
                vals={[billingInterval]}
              />
            </Grid>
            <Grid item xs={6}>
              <Fields.PvCurrencyMaskField
                disabled={true}
                value={monthlyRate || ''}
                label="Monthly Rate"
                name="monthly-rate"
              />
            </Grid>
            <Grid item xs={6}>
              {/* TODO: Properly update field to read-only */}
              <Fields.PvIntegerField
                disabled={true}
                label="Card Processing Fee"
                name="card-processing-fee"
                value={cardApplicationFeeRate || ''}
                vals={[cardApplicationFeeRate]}
              />
            </Grid>
            <Grid item xs={6}>
              {/* TODO: Properly update field to read-only */}
              <Fields.PvIntegerField
                disabled={true}
                label="ACH Processing Fee"
                name="ach-processing-fee"
                value={achApplicationFeeRate || ''}
                vals={[achApplicationFeeRate]}
              />
            </Grid>
            <Grid item xs={6}>
              <Fields.PvDateField
                disabled={true}
                clearable
                fullWidth
                label="Customer Start Date"
                name="customer-started-at"
                value={customerStartedAt || ''}
              />
            </Grid>
            <Grid item xs={6}>
              <Fields.PvDropDown
                vals={paymentProviderVals}
                disabled={!editing}
                label="Payment Provider"
                name="payment-provider"
                value={defaultPaymentProvider || ''}
                onChange={(e) => setDefaultPaymentProvider(e.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <Fields.PvTextField
                disabled={true}
                label="Connected Stripe Account"
                value={venue.stripeAccount?.name || ''}
                name="stripe-account-name"
                helperText={
                  <span>
                    {venue.stripeAccount?.stripeAccountId && (
                      <Link
                        href={`https://dashboard.stripe.com/connect/accounts/${venue.stripeAccount?.stripeAccountId}/activity`}
                      >
                        View Stripe Account
                      </Link>
                    )}
                  </span>
                }
              />
            </Grid>
            <Grid item xs={6}>
              <Fields.PvTextField
                disabled={true}
                label="Notification Email"
                value={venue.email || ''}
                name="email"
              />
            </Grid>
            <Grid item xs={6}>
              {!!trialEndDate && (
                <Fields.PvDateField
                  disabled={!editing}
                  fullWidth
                  label="Trial End Date"
                  name="trial-end-date"
                  value={trialEndDate || ''}
                  onChange={(d) => setTrialEndDate(d)}
                />
              )}
            </Grid>
            <Grid item xs={6}>
              <Fields.PvDropDown
                disabled={!editing}
                vals={availablePlanSetVals}
                label="Plan Set"
                name="plan-set"
                value={planSetId || ''}
                onChange={(e) => setPlanSetId(e.target.value)}
                helperText="Plan Sets are for venues that have made a deal with sales for a specific set of prices that is different from the prices for other venues in their organization"
              />
            </Grid>
            <Grid container>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={true}
                      color="primary"
                      className="basicFeaturesEnabled"
                      name="basicFeaturesEnabled"
                      checked={basicFeaturesEnabled}
                    />
                  }
                  label="Basic Features"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={true}
                      color="primary"
                      className="proFeaturesEnabled"
                      name="proFeaturesEnabled"
                      checked={proFeaturesEnabled}
                    />
                  }
                  label="Pro Features"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={true}
                      color="primary"
                      className="premiumFeaturesEnabled"
                      name="premiumFeaturesEnabled"
                      checked={premiumFeaturesEnabled}
                    />
                  }
                  label="Premium Features"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={true}
                      color="primary"
                      className="enterpriseFeaturesEnabled"
                      name="enterpriseFeaturesEnabled"
                      checked={enterpriseFeaturesEnabled}
                    />
                  }
                  label="Enterprise Features"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={!editing}
                      color="primary"
                      className="paymentsEnabled"
                      name="paymentsEnabled"
                      checked={paymentsEnabled}
                      onChange={(e) => setPaymentsEnabled(e.target.checked)}
                    />
                  }
                  label="Guest Payments Enabled"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={!editing}
                      color="primary"
                      className="achEnabled"
                      name="achEnabled"
                      checked={achEnabled}
                      onChange={(e) => setAchEnabled(e.target.checked)}
                    />
                  }
                  label="ACH Enabled"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={!editing}
                      color="primary"
                      className="liveAvailabilityEnabled"
                      name="liveAvailabilityEnabled"
                      checked={liveAvailability}
                      onChange={(e) => setLiveAvailability(e.target.checked)}
                    />
                  }
                  label="Live Availability"
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </EditablePanel>
    </div>
  );
};
